import { useContext, useEffect, useMemo, useState } from "react";

import {
  ArrowBack,
  Close,
  Delete,
  Description,
  Download,
  Map as MapIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  GetSubmissionResponsePortal,
  IntelImageMatchModel,
  OrgConfigModel,
  deleteIntelMatchReport,
  getIntelMatchReport,
  getIntelMatchReportPdf,
  getOrgConfigPortal,
  getSubmissionPortal,
  listIntelImageMatchByAttachment,
  patchIntelMatchReport,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useLocation, useParams, useNavigate } from "react-router-dom";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { APP_BAR_HEIGHT } from "@/layout/AppBar";

import ImageMatchMap, { MatchMapEntry } from "./ImageMatchMap";
import {
  extractCoordinates,
  extractPlaceRef,
  formatDate,
} from "./submissionFields";
import SubmissionSummaryCard from "./SubmissionSummaryCard";

interface MatchedReport {
  key: string;
  submission: GetSubmissionResponsePortal | null;
  attachmentUrl: string | null;
  attachmentFilename: string | null;
  matchNotes: string | null;
  matchedOn: string | null;
}

const ImageMatchReport = () => {
  const { orgId, attachmentId, matchReportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AuthStateContext);
  const { isDevEnv } = useDevEnvironment();
  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );
  const [loading, setLoading] = useState(true);
  const [sourceSubmission, setSourceSubmission] =
    useState<GetSubmissionResponsePortal | null>(null);
  const [sourceOrgConfig, setSourceOrgConfig] = useState<OrgConfigModel | null>(
    null,
  );
  const [userOrgConfig, setUserOrgConfig] = useState<OrgConfigModel | null>(
    null,
  );
  const [sourceAttachmentUrl, setSourceAttachmentUrl] = useState<string | null>(
    null,
  );
  const [sourceAttachmentFilename, setSourceAttachmentFilename] = useState<
    string | null
  >(null);
  const [matchedReports, setMatchedReports] = useState<MatchedReport[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [disseminationEmails, setDisseminationEmails] = useState<string[]>([]);
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");
  const [savedTitle, setSavedTitle] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [savedNotes, setSavedNotes] = useState<string>("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState<MatchMapEntry | null>(
    null,
  );
  // Agency crest URL keyed by the submitting user's org id.
  const [crestByOrgId, setCrestByOrgId] = useState<
    Record<string, string | null>
  >({});

  // Fetch match report details (dissemination emails, reference number, title, notes)
  useEffect(() => {
    if (!matchReportId) return;
    const loadReportDetails = async () => {
      try {
        const response = await getIntelMatchReport({
          path: { id: matchReportId },
        });
        setDisseminationEmails(response.data?.dissemination_emails ?? []);
        setReferenceNumber(response.data?.reference_number ?? null);
        const loadedTitle = response.data?.title ?? "";
        const loadedNotes = response.data?.notes ?? "";
        setTitle(loadedTitle);
        setSavedTitle(loadedTitle);
        setNotes(loadedNotes);
        setSavedNotes(loadedNotes);
      } catch {
        // ignore - not critical for page display
      }
    };
    loadReportDetails();
  }, [matchReportId]);

  useEffect(() => {
    if (!attachmentId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        // Live matches: the source of truth when viewing without a saved
        // report, and used to enrich saved-report items with match metadata.
        const matchResult = await listIntelImageMatchByAttachment({
          path: { attachment_id: attachmentId },
        });
        const liveMatches = matchResult.data?.matches ?? [];

        // The URL carries the attachment id as it was when the report was generated.
        // Attachment ids are version-scoped, so once a submission has been edited that
        // id is absent from the version we render below. Everything from here on uses
        // the id the API resolved forward, which the match and item ids also agree with.
        const sourceAttachmentId =
          matchResult.data?.resolved_attachment_id ?? attachmentId;

        const findLiveMatch = (otherAttachmentId: string | null) =>
          liveMatches.find(
            (m) =>
              (m.attachment_id_1 === sourceAttachmentId &&
                m.attachment_id_2 === otherAttachmentId) ||
              (m.attachment_id_2 === sourceAttachmentId &&
                m.attachment_id_1 === otherAttachmentId),
          ) ?? null;

        // The set of submissions to display. For a saved report we use its
        // stored items so it stays a snapshot in time even if a match was
        // later deleted. Without a saved report we derive from live matches.
        let sourceSubmissionId: string | null = null;
        let matchedRefs: {
          submissionId: string;
          attachmentId: string | null;
          liveMatch: IntelImageMatchModel | null;
          matchedOn: string | null;
        }[] = [];

        if (matchReportId) {
          const reportResult = await getIntelMatchReport({
            path: { id: matchReportId },
          });
          const items = reportResult.data?.items ?? [];
          const sourceItem = items.find(
            (i) => i.attachment_id === sourceAttachmentId,
          );
          sourceSubmissionId = sourceItem?.submission_id ?? null;
          matchedRefs = items
            .filter((i) => i.attachment_id !== sourceAttachmentId)
            .map((i) => {
              const liveMatch = findLiveMatch(i.attachment_id ?? null);
              return {
                submissionId: i.submission_id,
                attachmentId: i.attachment_id ?? null,
                liveMatch,
                // Prefer the snapshot; fall back to the live match for reports
                // generated before matched_on was tracked.
                matchedOn: i.matched_on ?? liveMatch?.time_created ?? null,
              };
            });
        } else {
          if (liveMatches.length === 0) {
            setLoading(false);
            return;
          }
          const firstMatch = liveMatches[0];
          sourceSubmissionId =
            firstMatch.attachment_id_1 === sourceAttachmentId
              ? firstMatch.submission_id_1
              : firstMatch.submission_id_2;
          matchedRefs = liveMatches.map((match) => {
            const isOnSide1 = match.attachment_id_1 === sourceAttachmentId;
            return {
              submissionId: isOnSide1
                ? match.submission_id_2
                : match.submission_id_1,
              attachmentId: isOnSide1
                ? match.attachment_id_2
                : match.attachment_id_1,
              liveMatch: match,
              matchedOn: match.time_created,
            };
          });
        }

        if (!sourceSubmissionId) {
          setLoading(false);
          return;
        }

        // Fetch source submission
        const sourceResult = await getSubmissionPortal({
          path: { submission_id: sourceSubmissionId },
          query: { version: "latest" },
        });
        const sourceSub = sourceResult.data ?? null;
        setSourceSubmission(sourceSub);

        // Find source attachment URL
        if (sourceSub) {
          const att = sourceSub.attachments.find(
            (a) => a.id === sourceAttachmentId,
          );
          setSourceAttachmentUrl(att?.presigned_get?.s3_presigned?.uri ?? null);
          setSourceAttachmentFilename(att?.filename ?? null);
        }

        // Fetch source org config (agency)
        if (orgId) {
          try {
            const orgResult = await getOrgConfigPortal({
              path: { org_id: orgId },
            });
            setSourceOrgConfig(orgResult.data ?? null);
          } catch {
            // ignore
          }
        }

        // Fetch current user's org config (CISO) for report branding
        if (currentUser?.rubi_org_id) {
          try {
            const userOrgResult = await getOrgConfigPortal({
              path: { org_id: currentUser.rubi_org_id },
            });
            setUserOrgConfig(userOrgResult.data ?? null);
          } catch {
            // ignore
          }
        }

        // Fetch each matched submission
        const enriched = await Promise.all(
          matchedRefs.map(async (ref) => {
            const key =
              ref.liveMatch?.id ?? `${ref.submissionId}:${ref.attachmentId}`;
            const matchNotes = ref.liveMatch?.notes ?? null;
            const matchedOn = ref.matchedOn;

            try {
              const subResult = await getSubmissionPortal({
                path: { submission_id: ref.submissionId },
                query: { version: "latest" },
              });
              const sub = subResult.data ?? null;
              const att = ref.attachmentId
                ? sub?.attachments.find((a) => a.id === ref.attachmentId)
                : undefined;

              return {
                key,
                submission: sub,
                attachmentUrl: att?.presigned_get?.s3_presigned?.uri ?? null,
                attachmentFilename: att?.filename ?? null,
                matchNotes,
                matchedOn,
              };
            } catch {
              return {
                key,
                submission: null,
                attachmentUrl: null,
                attachmentFilename: null,
                matchNotes,
                matchedOn,
              };
            }
          }),
        );
        setMatchedReports(enriched);
      } catch (err) {
        console.error("Failed to load match report data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [attachmentId, orgId, currentUser?.rubi_org_id, matchReportId]);

  // Resolve each submitting agency's crest, keyed by org id. Prefer the org's
  // configured logo; fall back to the bundled static service-logo; otherwise
  // leave null so the map renders an initials pin.
  useEffect(() => {
    const orgIds = new Set<string>();
    const collect = (s: GetSubmissionResponsePortal | null) => {
      if (s?.submitter_org_id) orgIds.add(s.submitter_org_id);
    };
    collect(sourceSubmission);
    matchedReports.forEach((m) => collect(m.submission));
    if (orgIds.size === 0) return;

    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(
        [...orgIds].map(async (id) => {
          try {
            const res = await getOrgConfigPortal({ path: { org_id: id } });
            const logo = res.data?.metadata?.logo_url?.trim();
            if (logo) return [id, logo] as const;
          } catch {
            // ignore; fall through to the static service-logo
          }
          return [id, getServiceConfig(id, isDevEnv).logoURL ?? null] as const;
        }),
      );
      if (!cancelled) setCrestByOrgId(Object.fromEntries(resolved));
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceSubmission, matchedReports, isDevEnv]);

  // The source submission plus every matched submission, decorated with the
  // coordinates and crest the map needs.
  const mapEntries: MatchMapEntry[] = useMemo(() => {
    const crestFor = (s: GetSubmissionResponsePortal | null) =>
      s?.submitter_org_id ? (crestByOrgId[s.submitter_org_id] ?? null) : null;
    const agencyOf = (s: GetSubmissionResponsePortal | null) =>
      s?.submitter_agency || "Unknown agency";

    const entries: MatchMapEntry[] = [];
    if (sourceSubmission) {
      entries.push({
        key: "source",
        submission: sourceSubmission,
        attachmentUrl: sourceAttachmentUrl,
        attachmentFilename: sourceAttachmentFilename,
        matchNotes: null,
        matchedOn: null,
        isSource: true,
        coords: extractCoordinates(sourceSubmission),
        placeRef: extractPlaceRef(sourceSubmission),
        crestUrl: crestFor(sourceSubmission),
        agencyName: agencyOf(sourceSubmission),
      });
    }
    matchedReports.forEach((m) => {
      entries.push({
        key: m.key,
        submission: m.submission,
        attachmentUrl: m.attachmentUrl,
        attachmentFilename: m.attachmentFilename,
        matchNotes: m.matchNotes,
        matchedOn: m.matchedOn,
        isSource: false,
        coords: extractCoordinates(m.submission),
        placeRef: extractPlaceRef(m.submission),
        crestUrl: crestFor(m.submission),
        agencyName: agencyOf(m.submission),
      });
    });
    return entries;
  }, [
    sourceSubmission,
    sourceAttachmentUrl,
    sourceAttachmentFilename,
    matchedReports,
    crestByOrgId,
  ]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleDeleteConfirm = async () => {
    if (!matchReportId) return;
    setIsDeleting(true);
    try {
      await deleteIntelMatchReport({
        path: { id: matchReportId },
      });
      toast.success("Match report deleted");
      setDeleteModalOpen(false);
      if (location.state?.fromList) {
        navigate(-1);
      } else {
        navigate(`/rubiintel/${orgId}/match-reports`);
      }
    } catch (error) {
      console.error("Error deleting match report:", error);
      toast.error("Failed to delete match report");
    } finally {
      setIsDeleting(false);
    }
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleDisseminationChange = async (
    _event: React.SyntheticEvent,
    newValue: string[],
  ) => {
    if (!matchReportId) return;

    if (newValue.length > disseminationEmails.length) {
      const added = newValue[newValue.length - 1].trim();
      if (!EMAIL_REGEX.test(added)) {
        toast.warning("Invalid email format");
        return;
      }
      if (
        disseminationEmails.some((e) => e.toLowerCase() === added.toLowerCase())
      ) {
        toast.warning("Duplicate email address");
        return;
      }
      const updated = [...disseminationEmails, added];
      setDisseminationEmails(updated);
      try {
        await patchIntelMatchReport({
          path: { id: matchReportId },
          body: { dissemination_emails: updated },
        });
      } catch {
        toast.error("Failed to update dissemination list");
      }
    } else {
      setDisseminationEmails(newValue);
      try {
        await patchIntelMatchReport({
          path: { id: matchReportId },
          body: { dissemination_emails: newValue },
        });
      } catch {
        toast.error("Failed to update dissemination list");
      }
    }
  };

  const sanitizeFilename = (s: string) =>
    s.replace(/[/\\:*?"<>|-]/g, "_").trim();

  const downloadPDF = async () => {
    if (!matchReportId) return;
    setIsDownloading(true);
    try {
      // Persist any pending title/notes edits so the generated PDF reflects
      // the latest values.
      const pendingPatch: { title?: string; notes?: string } = {};
      if (title !== savedTitle) pendingPatch.title = title;
      if (notes !== savedNotes) pendingPatch.notes = notes;
      if (Object.keys(pendingPatch).length > 0) {
        await patchIntelMatchReport({
          path: { id: matchReportId },
          body: pendingPatch,
        });
        if (pendingPatch.title !== undefined) setSavedTitle(title);
        if (pendingPatch.notes !== undefined) setSavedNotes(notes);
      }

      const response = await getIntelMatchReportPdf({
        path: { id: matchReportId },
      });
      const blob = response.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const trimmedTitle = title.trim();
      a.download = trimmedTitle
        ? `${sanitizeFilename(trimmedTitle)}.pdf`
        : `${referenceNumber ?? matchReportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  const saveTitle = async () => {
    if (!matchReportId) return;
    if (title === savedTitle) return;
    setIsSavingTitle(true);
    try {
      await patchIntelMatchReport({
        path: { id: matchReportId },
        body: { title },
      });
      setSavedTitle(title);
    } catch {
      toast.error("Failed to save title");
      setTitle(savedTitle);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const saveNotes = async () => {
    if (!matchReportId) return;
    if (notes === savedNotes) return;
    setIsSavingNotes(true);
    try {
      await patchIntelMatchReport({
        path: { id: matchReportId },
        body: { notes },
      });
      setSavedNotes(notes);
    } catch {
      toast.error("Failed to save notes");
      setNotes(savedNotes);
    } finally {
      setIsSavingNotes(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: 900,
          margin: "0 auto",
          p: 4,
        }}
      >
        {/* Action bar */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 3,
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
          <Box sx={{ flex: 1 }} />
          {matchReportId && (
            <Button
              variant="contained"
              disabled={isDownloading}
              startIcon={
                isDownloading ? <CircularProgress size={18} /> : <Download />
              }
              onClick={downloadPDF}
              sx={{ textTransform: "none" }}
            >
              {isDownloading ? "Generating..." : "Download PDF"}
            </Button>
          )}
          {matchReportId && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteModalOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          )}
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            setSelectedEntry(null);
          }}
          sx={{ mb: 3 }}
        >
          <Tab icon={<Description />} iconPosition="start" label="Report" />
          <Tab icon={<MapIcon />} iconPosition="start" label="Map" />
        </Tabs>

        {activeTab === 1 && (
          <ImageMatchMap
            entries={mapEntries}
            selectedKey={selectedEntry?.key ?? null}
            onSelect={setSelectedEntry}
          />
        )}

        {activeTab === 0 && (
          <>
            {/* Report Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                component="img"
                src="/service-logos/CISO-Logo.png"
                alt="CISO logo"
                sx={{ height: 60, mb: 1 }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Image Match Report
              </Typography>
              {referenceNumber && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {referenceNumber}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Generated {formatDate(new Date().toISOString())}
              </Typography>
              {sourceOrgConfig?.metadata?.city && (
                <Typography variant="body2" color="text.secondary">
                  {sourceOrgConfig.metadata.city}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Source Attachment */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Source Attachment
              </Typography>
              <SubmissionSummaryCard
                submission={sourceSubmission}
                attachmentUrl={sourceAttachmentUrl}
                attachmentFilename={sourceAttachmentFilename}
              />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Matched Submissions */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Matched Submissions ({matchedReports.length})
            </Typography>

            {matchedReports.map((item, index) => (
              <Box
                key={item.key}
                sx={{
                  mb: 3,
                  p: 2,
                  border: "1px solid var(--divider)",
                  borderRadius: 1,
                  pageBreakInside: "avoid",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, mb: 1.5 }}
                >
                  Match {index + 1}
                </Typography>
                <SubmissionSummaryCard
                  submission={item.submission}
                  attachmentUrl={item.attachmentUrl}
                  attachmentFilename={item.attachmentFilename}
                  matchNotes={item.matchNotes}
                  matchedOn={item.matchedOn}
                />
              </Box>
            ))}

            {matchedReports.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No matches found for this attachment.
              </Typography>
            )}

            {/* Dissemination List */}
            {matchReportId && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Dissemination List
                </Typography>
                <Autocomplete<string, true, false, true>
                  multiple
                  freeSolo
                  autoSelect
                  size="small"
                  options={[]}
                  value={disseminationEmails}
                  onChange={handleDisseminationChange}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        disseminationEmails.length === 0
                          ? "Add email addresses..."
                          : ""
                      }
                      size="small"
                    />
                  )}
                />

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Title
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={title}
                    placeholder="Add a title..."
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={saveTitle}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    disabled={isSavingTitle}
                    helperText={
                      title !== savedTitle
                        ? "Press Enter or click away to save"
                        : title.trim()
                          ? "Used as the PDF filename when downloaded"
                          : " "
                    }
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Notes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={notes}
                    placeholder="Add notes..."
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={saveNotes}
                    disabled={isSavingNotes}
                    helperText={
                      notes !== savedNotes ? "Click away to save" : " "
                    }
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={selectedEntry !== null}
        onClose={() => setSelectedEntry(null)}
        PaperProps={{
          sx: {
            // The app bar is fixed above the drawer (zIndex drawer + 1), so
            // start the panel below it to avoid clipping the header/crest.
            top: `${APP_BAR_HEIGHT}px`,
            height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
          },
        }}
      >
        <Box
          sx={{
            width: { xs: "85vw", sm: 440 },
            maxWidth: "100vw",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            p: 3,
          }}
        >
          {/* Header: crest + title/agency, with close */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              {selectedEntry?.crestUrl && (
                <Box
                  component="img"
                  src={selectedEntry.crestUrl}
                  alt={selectedEntry.agencyName}
                  sx={{
                    width: 56,
                    height: 56,
                    flexShrink: 0,
                    objectFit: "contain",
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.4 }}
                >
                  {selectedEntry?.isSource
                    ? "Source Submission"
                    : "Matched Submission"}
                </Typography>
                {selectedEntry?.agencyName && (
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {selectedEntry.agencyName}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton
              onClick={() => setSelectedEntry(null)}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable details so the footer link stays in view */}
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {selectedEntry && (
              <SubmissionSummaryCard
                submission={selectedEntry.submission}
                attachmentUrl={selectedEntry.attachmentUrl}
                attachmentFilename={selectedEntry.attachmentFilename}
                matchNotes={selectedEntry.matchNotes}
                matchedOn={selectedEntry.matchedOn}
                stacked
              />
            )}
          </Box>

          {selectedEntry?.submission && (
            <>
              <Divider sx={{ my: 2 }} />
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigate(
                    `/rubiintel/${orgId}/report/${selectedEntry.submission?.submission_id}`,
                  )
                }
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                View full report
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={deleteModalOpen}
        onClose={() => {
          if (!isDeleting) setDeleteModalOpen(false);
        }}
      >
        <DialogTitle>Delete Match Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this match report? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageMatchReport;
